import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api } from '../services/api';

// Constante para definir el tiempo máximo de validez del caché (24 horas)
const CACHE_MAX_AGE = 24 * 60 * 60 * 1000;

// Clave para almacenar en localStorage
const PRICE_CACHE_KEY = 'canvasoft_prices';

// Interfaz para los datos de precios
export interface PricePackage {
  id: number;
  nombre: string;
  cantidad: number;
  precioUsd: number;
  descuento: string;
}

// Interfaz para el objeto de caché
interface CachedPriceData {
  timestamp: number;
  data: PricePackage[];
}

interface PriceConfig {
  individual: PricePackage | null;
  pack5: PricePackage | null;
  pack10: PricePackage | null;
  isLoading: boolean;
  error: string | null;
  lastUpdated: number | null;
  source: 'server' | 'cache' | 'default' | null;
}

interface PriceContextType {
  pricePackages: PricePackage[] | null;
  priceConfig: PriceConfig;
  getPriceByType: (type: 'individual' | 'pack5' | 'pack10') => number;
  getDiscountByType: (type: 'individual' | 'pack5' | 'pack10') => number;
  refreshPrices: () => Promise<void>;
  lastUpdated: number | null;
  dataSource: 'server' | 'cache' | 'default' | null;
}

const PriceContext = createContext<PriceContextType | undefined>(undefined);

export const usePrices = (): PriceContextType => {
  const context = useContext(PriceContext);
  if (!context) {
    throw new Error('usePrices must be used within a PriceProvider');
  }
  return context;
};

interface PriceProviderProps {
  children: ReactNode;
}

export const PriceProvider: React.FC<PriceProviderProps> = ({ children }) => {
  const [pricePackages, setPricePackages] = useState<PricePackage[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);
  const [dataSource, setDataSource] = useState<'server' | 'cache' | 'default' | null>(null);
  
  // Precios y configuración estructurada para acceso más sencillo
  const [priceConfig, setPriceConfig] = useState<PriceConfig>({
    individual: null,
    pack5: null,
    pack10: null,
    isLoading: true,
    error: null,
    lastUpdated: null,
    source: null
  });

  // Función para procesar los datos de precios
  const processPriceData = (prices: PricePackage[], source: 'server' | 'cache' | 'default', timestamp: number) => {
    setPricePackages(prices);
    setLastUpdated(timestamp);
    setDataSource(source);
    
    // Organizar precios por tipo para acceso más fácil
    const individual = prices.find(pkg => pkg.cantidad === 1) || null;
    const pack5 = prices.find(pkg => pkg.cantidad === 5) || null;
    const pack10 = prices.find(pkg => pkg.cantidad === 10) || null;
    
    setPriceConfig({
      individual,
      pack5,
      pack10,
      isLoading: false,
      error: null,
      lastUpdated: timestamp,
      source
    });
  };

  // Función para cargar precios con estrategia de caché
  const loadPrices = async () => {
    setIsLoading(true);
    setError(null);
    
    // 1. Intentar cargar datos desde localStorage primero
    try {
      const cachedData = localStorage.getItem(PRICE_CACHE_KEY);
      if (cachedData) {
        const { timestamp, data } = JSON.parse(cachedData) as CachedPriceData;
        
        // Verificar si los datos son lo suficientemente recientes
        if (Date.now() - timestamp < CACHE_MAX_AGE) {
          console.log('Usando datos de precios en caché');
          processPriceData(data, 'cache', timestamp);
          
          // Ya no actualizamos en segundo plano para evitar llamadas repetidas
          // Solo si el caché está próximo a vencer (más de 20 horas)
          if (Date.now() - timestamp > (20 * 60 * 60 * 1000)) {
            console.log('Caché próximo a vencer, actualizando en segundo plano');
            refreshPricesInBackground();
          }
          
          setIsLoading(false);
          return;
        } else {
          console.log('Caché de precios expirado, obteniendo datos frescos');
        }
      }
    } catch (e) {
      console.warn('Error al leer precios almacenados en caché', e);
      // Continuar con la carga desde la API
    }
    
    // 2. Si no hay caché o está expirado, intentar cargar desde la API
    try {
      const prices = await api.getPricePackages();
      if (prices) {
        const now = Date.now();
        
        // Guardar en caché
        localStorage.setItem(PRICE_CACHE_KEY, JSON.stringify({
          timestamp: now,
          data: prices
        }));
        
        processPriceData(prices, 'server', now);
      } else {
        throw new Error('No se recibieron datos del servidor');
      }
    } catch (err) {
      console.error('Error al cargar precios desde la API:', err);
      
      // 3. Intenta usar caché antigua como último recurso
      try {
        const oldCache = localStorage.getItem(PRICE_CACHE_KEY);
        if (oldCache) {
          const { timestamp, data } = JSON.parse(oldCache) as CachedPriceData;
          console.warn('Usando datos de caché expirados como respaldo');
          processPriceData(data, 'cache', timestamp);
        } else {
          // 4. Si no hay caché, usar valores por defecto
          setError('No se pudieron cargar los precios');
          setDataSource('default');
          
          // Valores por defecto
          const defaultPrices: PricePackage[] = [
            { id: 1, nombre: 'Individual', cantidad: 1, precioUsd: 2.5, descuento: '0' },
            { id: 2, nombre: 'Paquete x5', cantidad: 5, precioUsd: 2.0, descuento: '20' },
            { id: 3, nombre: 'Paquete x10', cantidad: 10, precioUsd: 1.75, descuento: '30' }
          ];
          
          processPriceData(defaultPrices, 'default', 0);
          
          setPriceConfig(prev => ({
            ...prev,
            error: 'Error al obtener los precios del servidor. Usando valores predeterminados.',
            source: 'default'
          }));
        }
      } catch (e) {
        // 5. Si todo lo anterior falla, establecer error
        setError('Error al obtener los precios');
        setDataSource('default');
        
        // Valores por defecto en último caso
        const defaultPrices: PricePackage[] = [
          { id: 1, nombre: 'Individual', cantidad: 1, precioUsd: 2.5, descuento: '0' },
          { id: 2, nombre: 'Paquete x5', cantidad: 5, precioUsd: 2.0, descuento: '20' },
          { id: 3, nombre: 'Paquete x10', cantidad: 10, precioUsd: 1.75, descuento: '30' }
        ];
        
        processPriceData(defaultPrices, 'default', 0);
        
        setPriceConfig(prev => ({
          ...prev,
          error: 'Error al obtener los precios del servidor. Usando valores predeterminados.',
          source: 'default'
        }));
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  // Actualizar precios en segundo plano sin mostrar loading state
  const refreshPricesInBackground = async () => {
    console.log('Iniciando actualización de precios en segundo plano...');
    try {
      const prices = await api.getPricePackages();
      if (prices) {
        const now = Date.now();
        
        // Guardar en caché
        localStorage.setItem(PRICE_CACHE_KEY, JSON.stringify({
          timestamp: now,
          data: prices
        }));
        
        processPriceData(prices, 'server', now);
        console.log('Precios actualizados en segundo plano');
      }
    } catch (err) {
      console.warn('Error al actualizar precios en segundo plano:', err);
      // No cambiamos el estado de error para no afectar al usuario
    }
  };
  
  // Función pública para forzar actualización de precios
  const refreshPrices = async () => {
    setIsLoading(true);
    try {
      const prices = await api.getPricePackages();
      if (prices) {
        const now = Date.now();
        
        // Guardar en caché
        localStorage.setItem(PRICE_CACHE_KEY, JSON.stringify({
          timestamp: now,
          data: prices
        }));
        
        processPriceData(prices, 'server', now);
        console.log('Precios actualizados manualmente');
      } else {
        throw new Error('No se recibieron datos del servidor');
      }
    } catch (err) {
      console.error('Error al actualizar precios:', err);
      setError('Error al actualizar precios desde el servidor');
    } finally {
      setIsLoading(false);
    }
  };

  // Cargar precios una sola vez al iniciar la aplicación
  useEffect(() => {
    loadPrices();
  }, []);
  
  // Función auxiliar para obtener el precio según el tipo de paquete
  const getPriceByType = (type: 'individual' | 'pack5' | 'pack10'): number => {
    // Valores por defecto en caso de error
    const defaultPrices = {
      individual: 2.5,
      pack5: 2.0,
      pack10: 1.75
    };
    
    switch (type) {
      case 'individual':
        return priceConfig.individual?.precioUsd || defaultPrices.individual;
      case 'pack5':
        return priceConfig.pack5?.precioUsd || defaultPrices.pack5;
      case 'pack10':
        return priceConfig.pack10?.precioUsd || defaultPrices.pack10;
      default:
        return defaultPrices.individual;
    }
  };
  
  // Función auxiliar para obtener el descuento según el tipo de paquete
  const getDiscountByType = (type: 'individual' | 'pack5' | 'pack10'): number => {
    // Valores por defecto en caso de error
    const defaultDiscounts = {
      individual: 0,
      pack5: 0.20,  // 20%
      pack10: 0.30  // 30%
    };
    
    switch (type) {
      case 'individual':
        return priceConfig.individual ? parseFloat(priceConfig.individual.descuento) / 100 : defaultDiscounts.individual;
      case 'pack5':
        return priceConfig.pack5 ? parseFloat(priceConfig.pack5.descuento) / 100 : defaultDiscounts.pack5;
      case 'pack10':
        return priceConfig.pack10 ? parseFloat(priceConfig.pack10.descuento) / 100 : defaultDiscounts.pack10;
      default:
        return 0;
    }
  };

  return (
    <PriceContext.Provider value={{ 
      pricePackages, 
      priceConfig,
      getPriceByType,
      getDiscountByType,
      refreshPrices,
      lastUpdated,
      dataSource
    }}>
      {children}
    </PriceContext.Provider>
  );
};